import { useEffect, useState } from "react";
import {
  Accordion,
  Alert,
  Anchor,
  Box,
  Center,
  Container,
  Flex,
  Group,
  Image,
  Loader,
  PasswordInput, // pragma: allowlist secret
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconBuildingStore,
  IconChevronDown,
  IconDiscountCheckFilled,
  IconInfoCircle,
  IconLock,
  IconMail,
  IconUserCircle,
} from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import SmartWizLogo from "../../../../components/Logos/SmartWizLogo";
import { GoogleLogo } from "../../../../components/Logos/GoogleLogo";
import "./BusinessLogin.scss";
import { safeGetParam } from "@/utils/constants/Authentication";
import { resendVerification } from "../../api";
import { useLoginUser } from "../../hooks/useLoginUser";
import { useBranding } from "../../hooks/useBranding";
import { useGoogleSignIn } from "../../hooks/useGoogleSignIn";
import type { LoginApiResponse, RequiresAction } from "../../types/login.types";
import ExpiredSubscription from "../../components/LoginView/ExpiredSubscription";
import FailedPayment from "../../components/LoginView/FailedPayment";
import { hideNotification, showNotification } from "@mantine/notifications";
import AccountVerification from "@/pages/VerificationPage/components/AccountVerificationScreen";
import loginHero from "@/assets/login-hero.png";
import creationHero from "@/assets/creation-hero.png";
import { lightenHex } from "@/utils/helper";
import AuthLogo from "@/components/WhiteLabel/AuthLogo";
import BrandedAnchor from "@/components/WhiteLabel/BrandedAnchor";
import { AppRoutes } from "@/constants.ts";
import { useAuthStore } from "@utils/stores/authStore.ts";
import { safeLoginRedirectPath } from "../../utils/redirect";
import { isContractSubscriptionMismatch } from "@smartwiz/shared-types";
import {
  listRequiredActions,
  redirectForRequiredActions,
} from "../../utils/requiredActionRouting";

const BusinessLogin = (): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const redirectPath = safeLoginRedirectPath(queryParams.get("redirect"));
  const [email, setEmail] = useState(safeGetParam(queryParams, "email"));
  const [password, setPassword] = useState(""); // pragma: allowlist secret
  const {
    businessInfo,
    loading: businessLoading,
    error: businessError,
  } = useBranding();

  const { mutate, isPending, actions, actionMessages, businessSlug } =
    useLoginUser({ redirectPath });

  const {
    user,
    refreshAuth,
    loading: authLoading,
    billingContext,
  } = useAuthStore();

  // Check if user is already authenticated when component mounts
  // This is important for when the page refreshes after authentication via extension
  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      try {
        // Always check auth status on mount, even if we're on a login page
        // This ensures we pick up the token from cookies after extension authentication
        await refreshAuth();
      } catch (error) {
        // If auth check fails, user is not authenticated, stay on login page
        console.log("User not authenticated, staying on login page");
      }
    };

    checkAuthAndRedirect();
  }, []); // Only run on mount

  useEffect(() => {
    if (!user || authLoading) {
      return;
    }
    if (isContractSubscriptionMismatch(billingContext)) {
      navigate(AppRoutes.billingAccountIssue, { replace: true });
      return;
    }
    if (actions.length === 0) {
      navigate(redirectPath ?? AppRoutes.root, { replace: true });
    }
  }, [
    actions.length,
    authLoading,
    billingContext,
    navigate,
    redirectPath,
    user,
  ]);

  // Google Sign-In handler (business context from URL param; no business picker here)
  const handleGoogleSignInSuccess = (
    response: LoginApiResponse,
    _credential?: unknown
  ) => {
    if (
      "status" in response.content &&
      response.content.status === "business_selection_required"
    ) {
      return; // Should not happen when businessSlug is in URL
    }
    const content = response.content as {
      requiresAction?: RequiresAction;
    };
    if (redirectForRequiredActions(content.requiresAction)) {
      return;
    }

    const requiredActions = listRequiredActions(content.requiresAction);

    if (requiredActions.length > 0) {
      return;
    }
    if (redirectPath) {
      window.location.href = redirectPath;
    }
  };

  const handleGoogleSignInError = (error: unknown) => {
    const err = error as { content?: { message?: string }; message?: string };
    showNotification({
      title: "Google Sign-In Failed",
      message:
        err?.content?.message ||
        err?.message ||
        "Failed to sign in with Google",
      color: "red",
    });
  };

  const { triggerSignIn: triggerGoogleSignIn } = useGoogleSignIn(
    handleGoogleSignInSuccess,
    handleGoogleSignInError,
    {
      businessSlug,
      allowCreate: !!businessSlug,
      skipRedirect: Boolean(redirectPath),
    }
  );

  const handleActionNotifications = (email: string) => {
    if (actions.includes("emailVerification")) {
      resendVerification(email);
      showNotification({
        title: "Email Verification Required",
        message:
          actionMessages?.emailVerification ||
          "Please verify your email address",
        color: "orange",
      });
      return (
        <AccountVerification
          onComplete={() => {}}
          redirectPath={AppRoutes.home}
        />
      );
    } else if (actions.includes("subscriptionActivation")) {
      showNotification({
        title: "Subscription Expired",
        message:
          actionMessages?.subscriptionActivation ||
          "Your subscription has expired. Please update your payment method.",
        color: "orange",
      });
      return <ExpiredSubscription email={email} />;
    } else if (actions.includes("paymentUpdate")) {
      showNotification({
        title: "Payment Update Needed",
        message:
          actionMessages?.paymentUpdated ||
          "Please update your payment method.",
        color: "orange",
      });
      return <FailedPayment email={email} />;
    }
    return null;
  };

  useEffect(() => {
    if (isPending) {
      showNotification({
        id: "login-pending-notification",
        title: "Signing in...",
        message: "Please wait while we log you in.",
        loading: true,
        autoClose: false,
      });
    } else {
      hideNotification("login-pending-notification");
    }
  }, [isPending]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    const normalizedEmail = email?.toLowerCase();
    mutate({
      email: normalizedEmail,
      password,
      skipRedirect: Boolean(redirectPath),
    });
  };

  if (businessLoading) {
    return (
      <Container
        className=""
        size="sm"
        style={{ textAlign: "center", height: "100%" }}
      >
        <Center style={{ height: "100vh" }}>
          <SmartWizLogo /> <Loader size="md" color="blue" ml={10} />
        </Center>
      </Container>
    );
  }

  if (businessError) {
    return (
      <Container className="auth-form-container" size="sm">
        <Text c="red" size="lg" ta="center">
          {businessError}
        </Text>
      </Container>
    );
  }

  // Check for actions and return appropriate component
  const actionComponent = handleActionNotifications(email);
  if (actionComponent) {
    return actionComponent;
  }

  const lighterColor = lightenHex(
    businessInfo?.theme?.primaryColor || "#47a7eb",
    0.5
  ); // 20% lighter

  // Business Login form
  return (
    <div className="login-container">
      <Stack
        className="business-login-container"
        align="stretch"
        justify="start"
        style={{
          background: businessSlug
            ? `linear-gradient(180deg, rgba(240, 240, 240, 0.5) 30%, ${businessInfo?.theme?.primaryColor ? lighterColor : "#0077CC"}80 100%)`
            : "linear-gradient(180deg,rgb(32, 54, 150) -10%, #0077cc 50%, #444ca0 100%)",
        }}
      >
        <Stack align="center" justify="center">
          <div>
            <AuthLogo
              logoUrl={businessInfo?.logoUrl}
              businessSlug={businessSlug}
              primaryColor={businessInfo?.theme?.primaryColor}
              size={72}
              isSmall={true}
              alt="Business Logo"
              onError={(e) => {
                const imgElement = e.target as HTMLImageElement;
                console.error("Error loading business logo:", {
                  src: imgElement.src,
                  error: e,
                  businessInfo,
                });
              }}
            />
          </div>
          <Title
            ta="center"
            className="business-login-title"
            c={businessSlug ? "black" : "white"}
          >
            {businessSlug
              ? "Let's Get Your Taxes Done Right"
              : "File returns in minutes!"}
          </Title>
          <div className="secure">
            <div
              className="secure-icon"
              style={{
                color: businessSlug
                  ? businessInfo?.theme?.primaryColor ||
                    "var(--primary-button-color)"
                  : "white",
              }}
            >
              <IconDiscountCheckFilled />
            </div>
            <Text fw={600} c={businessSlug ? "black" : "white"}>
              Secure, Compliant, and Reliable
            </Text>
          </div>
        </Stack>
        <div className="login-img-container">
          {businessSlug ? (
            <Image src={loginHero} alt="Login Image" height="100%" />
          ) : (
            <Image src={creationHero} alt="Login Image" height="100%" />
          )}
        </div>
      </Stack>
      <Container className="auth-form-container" size="sm">
        <form onSubmit={handleSubmit}>
          <div className="mobile-fallback-logo">
            <AuthLogo
              logoUrl={businessInfo?.logoUrl}
              businessSlug={businessSlug}
              primaryColor={businessInfo?.theme?.primaryColor}
              size={72}
              isSmall={true}
              alt="Business Logo"
            />
          </div>
          {businessInfo?.businessName && (
            <Text ta="center" size="md" c="dimmed">
              Welcome back
            </Text>
          )}
          <Title className="title">
            {businessInfo?.businessName
              ? `Sign in to ${businessInfo.businessName}`
              : "Welcome back!"}
          </Title>
          <Text c="dimmed" size="sm">
            Sign in with Google or use your email and password
          </Text>

          {queryParams.get("existingAccount") === "1" && (
            <Alert
              variant="light"
              title={
                <Group align="center" gap="xs" justify="center">
                  <IconInfoCircle size={16} />
                  This account already exists
                </Group>
              }
              radius="md"
              mt="md"
              mb="xs"
            >
              Please sign in with your password to access your account.
            </Alert>
          )}

          <Stack className="stack styled-inputs" gap="md" p={8}>
            <button
              type="button"
              onClick={triggerGoogleSignIn}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #dadce0",
                backgroundColor: "white",
                color: "#3c4043",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <GoogleLogo
                width={18}
                height={18}
                style={{ marginRight: "8px" }}
              />
              Sign in with Google
            </button>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "16px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: "#e0e0e0",
                }}
              />
              <Text
                size="sm"
                c="dimmed"
                style={{ margin: "0 16px", whiteSpace: "nowrap" }}
              >
                or
              </Text>
              <div
                style={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: "#e0e0e0",
                }}
              />
            </div>
            <TextInput
              leftSection={<IconMail color="#A3A9B0" />}
              placeholder="Enter your account email"
              name="email"
              radius="md"
              type="text"
              value={email}
              required
              variant="unstyled"
              onChange={(e) => setEmail(e.target.value)}
            />
            <PasswordInput // pragma: allowlist secret
              leftSection={<IconLock color="#A3A9B0" />}
              placeholder="Enter password"
              name="password"
              className="password-field"
              radius="md"
              type="password"
              value={password}
              required
              variant="unstyled"
              style={{
                outline: "none",
                border: "none",
                boxShadow: "none",
              }}
              onChange={(e) => setPassword(e.target.value)} // pragma: allowlist secret
            />
            {!businessSlug && (
              <BrandedAnchor // pragma: allowlist secret
                className="forgot-password"
                businessSlug={businessSlug}
                businessInfo={businessInfo}
                label="Forgot Password?" // pragma: allowlist secret
                align="left"
              />
            )}
            <button
              className="sign-in"
              type="submit"
              disabled={isPending}
              style={{
                backgroundColor: businessInfo?.theme?.primaryColor || undefined,
                color: "white",
                marginTop: "16px",
                opacity: isPending ? 0.6 : 1,
                cursor: isPending ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {isPending && <Loader size="sm" color="white" />}
              {isPending ? "Signing in..." : "Sign in"}
            </button>
            {!businessSlug ? (
              <Box mt={16}>
                <Text ta="left" fz={16} fw={600} mb={8}>
                  Don't have an account?
                </Text>
                <Accordion variant="separated" radius="md">
                  <Accordion.Item value="client" bg={"#FFF0DB"}>
                    <Accordion.Control
                      icon={<IconUserCircle color={"#E78A00"} />}
                      chevron={<IconChevronDown color={"#E78A00"} />}
                    >
                      <Text c={"#E78A00"}>I'm a client</Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Text ta="left" fz={14}>
                        Reach out to your tax pro for their invite link to get
                        started.
                      </Text>
                    </Accordion.Panel>
                  </Accordion.Item>
                  <Accordion.Item value="taxpro" bg={"#EEF9FF"}>
                    <Accordion.Control
                      icon={<IconBuildingStore color={"#0077CC"} />}
                      chevron={<IconChevronDown color={"#0077CC"} />}
                    >
                      <Text c={"#0077CC"}>I'm a Tax Pro</Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Text ta="left" fz={14}>
                        Running a tax business?{" "}
                        <Anchor
                          href={AppRoutes.gettingStarted}
                          c={"#0077CC"}
                          inherit
                        >
                          Create your SmartWiz account
                        </Anchor>
                      </Text>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </Box>
            ) : (
              <span className="link-container">
                <BrandedAnchor
                  className="forgot-password"
                  businessSlug={businessSlug}
                  businessInfo={businessInfo}
                  label="Forgot Password?" // pragma: allowlist secret
                />
                <BrandedAnchor
                  className="link"
                  businessSlug={businessSlug}
                  businessInfo={businessInfo}
                  label="Create Account"
                />
              </span>
            )}
            <Flex justify="center" align="center" gap={8} mt={16}>
              <Text fz={14} fw={500}>
                Powered by
              </Text>{" "}
              <SmartWizLogo isLoginPage={true} />
            </Flex>
          </Stack>
        </form>
      </Container>
    </div>
  );
};

export default BusinessLogin;
